import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";

// Phase 2 — backend now exposes dedicated /api/donations/* endpoints that
// enforce role + dept-membership access control AND server-side PII
// redaction (donor consent, recipient privacy). Direct /api/storage access
// to the 3 donation keys is now blocked (403) — this client must use the
// dedicated endpoints only.

async function _json(res, fallback) {
  if (res.status === 404) return fallback;
  if (res.status === 403) throw new Error("403");
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || res.statusText);
  }
  return res.json();
}

const H = () => ({ "Content-Type": "application/json", ...authHeaders() });

export async function apiListIntakes() {
  try {
    const res = await kcFetch(`${API_BASE}/donations/intakes`, { headers: { ...authHeaders() } });
    return await _json(res, []);
  } catch (e) {
    if (e.message === "403") return null; // signal "no private access"
    throw e;
  }
}
export async function apiListPublicIntakes() {
  const res = await kcFetch(`${API_BASE}/donations/intakes/public`, { headers: { ...authHeaders() } });
  return _json(res, []);
}
export async function apiCreateIntake(payload) {
  const res = await kcFetch(`${API_BASE}/donations/intakes`, { method: "POST", headers: H(), body: JSON.stringify(payload) });
  return _json(res, null);
}
export async function apiUpdateIntake(id, payload) {
  const res = await kcFetch(`${API_BASE}/donations/intakes/${id}`, { method: "PUT", headers: H(), body: JSON.stringify(payload) });
  return _json(res, null);
}
export async function apiDeleteIntake(id) {
  const res = await kcFetch(`${API_BASE}/donations/intakes/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
  return _json(res, null);
}

export async function apiListDistributions() {
  try {
    const res = await kcFetch(`${API_BASE}/donations/distributions`, { headers: { ...authHeaders() } });
    return await _json(res, []);
  } catch (e) {
    if (e.message === "403") return null;
    throw e;
  }
}
export async function apiCreateDistribution(payload) {
  const res = await kcFetch(`${API_BASE}/donations/distributions`, { method: "POST", headers: H(), body: JSON.stringify(payload) });
  return _json(res, null);
}
export async function apiUpdateDistribution(id, payload) {
  const res = await kcFetch(`${API_BASE}/donations/distributions/${id}`, { method: "PUT", headers: H(), body: JSON.stringify(payload) });
  return _json(res, null);
}
export async function apiDeleteDistribution(id) {
  const res = await kcFetch(`${API_BASE}/donations/distributions/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
  return _json(res, null);
}

export async function apiListNeeds() {
  try {
    const res = await kcFetch(`${API_BASE}/donations/needs`, { headers: { ...authHeaders() } });
    return await _json(res, []);
  } catch (e) {
    if (e.message === "403") return null;
    throw e;
  }
}
export async function apiCreateNeed(payload) {
  const res = await kcFetch(`${API_BASE}/donations/needs`, { method: "POST", headers: H(), body: JSON.stringify(payload) });
  return _json(res, null);
}
export async function apiUpdateNeed(id, payload) {
  const res = await kcFetch(`${API_BASE}/donations/needs/${id}`, { method: "PUT", headers: H(), body: JSON.stringify(payload) });
  return _json(res, null);
}
export async function apiDeleteNeed(id) {
  const res = await kcFetch(`${API_BASE}/donations/needs/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
  return _json(res, null);
}

export async function apiInquireOnListing(intakeId) {
  const res = await kcFetch(`${API_BASE}/donations/inquire`, { method: "POST", headers: H(), body: JSON.stringify({ intakeId }) });
  return _json(res, null);
}

export async function apiDonationsReport({ from, to, nameMode }) {
  const qs = new URLSearchParams();
  if (from) qs.set("frm", from);
  if (to)   qs.set("to", to);
  if (nameMode) qs.set("nameMode", nameMode);
  const res = await kcFetch(`${API_BASE}/donations/reports/data?${qs}`, { headers: { ...authHeaders() } });
  return _json(res, null);
}
export async function apiDonationsArchive() {
  try {
    const res = await kcFetch(`${API_BASE}/donations/reports/archive`, { headers: { ...authHeaders() } });
    return await _json(res, []);
  } catch (e) {
    if (e.message === "403") return null;
    throw e;
  }
}

// Constants used across the module
export const CATEGORIES = ["Clothing", "Food", "Furniture", "Monetary", "Other"];
export const FUNDS = ["General Fund", "Building Fund", "Missions", "Food Bank", "Clothing Drive", "Other"];
export const CONDITIONS = ["New", "Like New", "Good", "Fair", "Needs Repair"];
export const URGENCY_LEVELS = ["High", "Medium", "Low"];
export const INTAKE_STATUSES = ["Logged", "Available", "Reserved", "Distributed", "Archived"];
export const DISTRIBUTION_STATUSES = ["Pending", "In Progress", "Distributed"];
export const NEED_STATUSES = ["Open", "In Progress", "Fulfilled"];

// Public-listing status is derived from the intake + linked distribution
// (the public endpoint pre-strips donor info; the derivation happens here):
//   • No distribution yet → Available
//   • Distribution exists in Pending / In Progress → Claimed
//   • Distribution complete (Distributed) → Distributed
export function publicStatusForIntake(intake, distributions) {
  const dist = (distributions || []).find(d => d.intakeId === intake.id);
  if (!dist) return "Available";
  if (dist.status === "Distributed") return "Distributed";
  return "Claimed";
}

export const CATEGORY_COLOR = {
  Clothing:  "#60a5fa",
  Food:      "#4ade80",
  Furniture: "#f59e0b",
  Monetary:  "var(--kc-accent)",
  Other:     "#a78bfa",
};

export const PUBLIC_STATUS_STYLE = {
  Available:   { bg: "rgba(74,222,128,0.16)", color: "#4ade80", border: "rgba(74,222,128,0.45)" },
  Claimed:     { bg: "color-mix(in srgb, var(--kc-accent) 16%, transparent)", color: "var(--kc-accent)", border: "color-mix(in srgb, var(--kc-accent) 45%, transparent)" },
  Distributed: { bg: "rgba(255,255,255,0.06)", color: "var(--kc-sub)", border: "rgba(255,255,255,0.12)" },
};
